/**
 * The game's theme music — an original composition, written entirely as note
 * data (this file), scheduled by `sequencer.ts` and synthesized by
 * `engine.ts` from oscillators and noise. `docs/DESIGN.md`'s Legal section:
 * "Do not use... Korobeiniki or any arrangement of it. All art and audio are
 * original." Nothing here is transcribed from, or approximates, any existing
 * piece — it is composed for this game, in A natural minor, and the owner
 * owns it outright (`docs/decisions/0042`).
 *
 * ## Shape
 *
 * - **128 BPM, 4/4, A natural minor** (A B C D E F G — no accidentals). A
 *   danger layer nudges the tempo to 128 × `DANGER_TEMPO_MULTIPLIER` ≈
 *   143 BPM rather than a dramatic jump — "raise tempo slightly" per the
 *   brief.
 * - **16 bars (64 beats), four 4-bar phrases**, the loop `sequencer.ts`
 *   schedules on repeat:
 *   - **A** (bars 1–4, `MOTIF_A`) — the main riff, syncopated eighth notes
 *     with short rests, low-to-mid register.
 *   - **B** (bars 5–8, `MOTIF_B`) — the lift: an octave up, denser (no
 *     rests), the same harmonic rhythm.
 *   - **A′** (bars 9–12) — `MOTIF_A` again, the riff's return.
 *   - **C** (bars 13–16, `MOTIF_C`) — a descending turnaround that resolves
 *     onto A4 exactly at beat 64, the same note `MOTIF_A` opens on, so the
 *     loop seam reads as a phrase ending and starting rather than a splice.
 * - **Harmony**: `Am F C G` for three phrases, `F G Am Am` for the
 *   turnaround — one chord per bar, root/third/fifth voiced as a sustained
 *   pad, re-struck every two beats.
 * - **Bass**: an eighth-note root/fifth/octave pattern under every bar,
 *   driving and continuous — the synthwave low end.
 * - **Percussion**: four-on-the-floor kick, a backbeat snare on 2 and 4, and
 *   eighth-note hats — built once per bar and repeated for all sixteen
 *   (`buildPercussion` below), not sixteen separate hand-written bars: the
 *   bar-length pattern *is* the composed data.
 * - **Danger layer**: a fast eighth-note arpeggio over the same harmony,
 *   layered in only when `sequencer.ts`'s `selectMusicLayer` says the stack
 *   is high — see `danger.ts`.
 *
 * Every pitched track is built from small chord tables (`BASS_CHORDS`,
 * `PAD_CHORDS`, `ARP_CHORDS`) plus the four explicit melody motifs, so the
 * "no accidental long silence or malformed duration" test
 * (`test/audio/composition.test.ts`) is checking real composed data, not a
 * placeholder.
 */
import { isNoteName, noteFreq } from './notes.js';

export type VoiceId = 'square' | 'triangle' | 'saw';
export type DrumId = 'kick' | 'snare' | 'hat';
export type MusicLayer = 'base' | 'danger';

export interface NoteEvent {
  /** Start time, in beats from the loop's own start (0-based, `[0, LOOP_BEATS)`). */
  readonly beat: number;
  readonly durationBeats: number;
  readonly note: string;
  readonly octave: number;
  readonly voice: VoiceId;
  /** 0..1. */
  readonly velocity: number;
}

export interface DrumHit {
  readonly beat: number;
  readonly drum: DrumId;
  readonly velocity: number;
}

export interface Composition {
  readonly title: string;
  readonly key: string;
  readonly bpm: number;
  readonly beatsPerBar: number;
  readonly bars: number;
  readonly melody: readonly NoteEvent[];
  readonly bass: readonly NoteEvent[];
  readonly chords: readonly NoteEvent[];
  readonly percussion: readonly DrumHit[];
  readonly dangerLayer: readonly NoteEvent[];
  readonly dangerTempoMultiplier: number;
}

export const BEATS_PER_BAR = 4;
export const BAR_COUNT = 16;
export const LOOP_BEATS = BEATS_PER_BAR * BAR_COUNT; // 64
export const BPM = 128;
export const DANGER_TEMPO_MULTIPLIER = 1.12; // "raise tempo slightly" — not a genre change.

interface ChordSpec {
  readonly root: readonly [string, number];
  readonly third: readonly [string, number];
  readonly fifth: readonly [string, number];
}

/** Root/third/fifth for the bassline's own low register. */
const BASS_CHORDS: Readonly<Record<string, ChordSpec>> = {
  Am: { root: ['A', 2], third: ['C', 3], fifth: ['E', 3] },
  F: { root: ['F', 2], third: ['A', 2], fifth: ['C', 3] },
  C: { root: ['C', 3], third: ['E', 3], fifth: ['G', 3] },
  G: { root: ['G', 2], third: ['B', 2], fifth: ['D', 3] },
};

/** Root/third/fifth for the sustained pad, one octave above the bass. */
const PAD_CHORDS: Readonly<Record<string, ChordSpec>> = {
  Am: { root: ['A', 3], third: ['C', 4], fifth: ['E', 4] },
  F: { root: ['F', 3], third: ['A', 3], fifth: ['C', 4] },
  C: { root: ['C', 4], third: ['E', 4], fifth: ['G', 4] },
  G: { root: ['G', 3], third: ['B', 3], fifth: ['D', 4] },
};

/** One chord per bar, in order — three phrases of `Am F C G`, then the `F G Am Am` turnaround. */
const PROGRESSION: readonly string[] = [
  'Am',
  'F',
  'C',
  'G',
  'Am',
  'F',
  'C',
  'G',
  'Am',
  'F',
  'C',
  'G',
  'F',
  'G',
  'Am',
  'Am',
];

/** `beat`/`octave` tuple shorthand used by the motifs and generators below. */
type Step = readonly [beat: number, durationBeats: number, note: string | null, octave: number];

function toNotes(
  steps: readonly Step[],
  voice: VoiceId,
  velocity: number,
  barOffsetBeats = 0,
): NoteEvent[] {
  const out: NoteEvent[] = [];
  for (const [beat, durationBeats, note, octave] of steps) {
    if (note === null) continue; // a rest — deliberately absent from the data, not a zero-duration note.
    out.push({ beat: beat + barOffsetBeats, durationBeats, note, octave, voice, velocity });
  }
  return out;
}

/**
 * Phrase A — the main riff (bars 1–4). Syncopated eighth notes with a short
 * rest mid-bar; the harmony under it is `Am F C G`.
 */
const MOTIF_A: readonly Step[] = [
  // Bar 1 — Am
  [0.0, 0.5, 'A', 4],
  [0.5, 0.5, 'C', 5],
  [1.0, 0.5, 'E', 5],
  [1.5, 0.5, null, 0],
  [2.0, 0.5, 'D', 5],
  [2.5, 0.5, 'C', 5],
  [3.0, 1.0, 'A', 4],
  // Bar 2 — F
  [4.0, 0.5, 'F', 4],
  [4.5, 0.5, 'A', 4],
  [5.0, 0.5, 'C', 5],
  [5.5, 0.5, null, 0],
  [6.0, 0.5, 'A', 4],
  [6.5, 0.5, 'F', 4],
  [7.0, 0.75, 'C', 5],
  [7.75, 0.25, 'A', 4],
  // Bar 3 — C
  [8.0, 0.5, 'C', 5],
  [8.5, 0.5, 'E', 5],
  [9.0, 0.5, 'G', 5],
  [9.5, 0.5, null, 0],
  [10.0, 0.5, 'E', 5],
  [10.5, 0.5, 'C', 5],
  [11.0, 1.0, 'G', 4],
  // Bar 4 — G
  [12.0, 0.5, 'G', 4],
  [12.5, 0.5, 'B', 4],
  [13.0, 0.5, 'D', 5],
  [13.5, 0.5, null, 0],
  [14.0, 0.5, 'B', 4],
  [14.5, 0.5, 'G', 4],
  [15.0, 0.75, 'D', 5],
  [15.75, 0.25, 'B', 4],
];

/**
 * Phrase B — the lift (bars 5–8). An octave up from A, no rests: this is the
 * loop's busiest, brightest four bars. Same harmony as phrase A.
 */
const MOTIF_B: readonly Step[] = [
  // Bar 5 — Am
  [16.0, 0.5, 'E', 5],
  [16.5, 0.5, 'A', 5],
  [17.0, 0.5, 'C', 6],
  [17.5, 0.5, 'A', 5],
  [18.0, 0.5, 'E', 5],
  [18.5, 0.5, 'C', 5],
  [19.0, 1.0, 'A', 4],
  // Bar 6 — F
  [20.0, 0.5, 'F', 5],
  [20.5, 0.5, 'A', 5],
  [21.0, 0.5, 'C', 6],
  [21.5, 0.5, 'A', 5],
  [22.0, 0.5, 'F', 5],
  [22.5, 0.5, 'C', 5],
  [23.0, 1.0, 'A', 4],
  // Bar 7 — C
  [24.0, 0.5, 'G', 5],
  [24.5, 0.5, 'C', 6],
  [25.0, 0.5, 'E', 6],
  [25.5, 0.5, 'C', 6],
  [26.0, 0.5, 'G', 5],
  [26.5, 0.5, 'E', 5],
  [27.0, 1.0, 'C', 5],
  // Bar 8 — G
  [28.0, 0.5, 'D', 5],
  [28.5, 0.5, 'G', 5],
  [29.0, 0.5, 'B', 5],
  [29.5, 0.5, 'G', 5],
  [30.0, 0.5, 'D', 5],
  [30.5, 0.5, 'B', 4],
  [31.0, 1.0, 'G', 4],
];

/**
 * Phrase C — the turnaround (bars 13–16). A descending run over `F G Am Am`
 * that lands on A4 exactly at beat 64 — the same note `MOTIF_A` opens on —
 * so the loop seam is a resolution, not a splice.
 */
const MOTIF_C: readonly Step[] = [
  // Bar 13 — F
  [48.0, 0.5, 'C', 6],
  [48.5, 0.5, 'A', 5],
  [49.0, 0.5, 'F', 5],
  [49.5, 0.5, null, 0],
  [50.0, 0.5, 'A', 5],
  [50.5, 0.5, 'F', 5],
  [51.0, 1.0, 'C', 5],
  // Bar 14 — G
  [52.0, 0.5, 'B', 5],
  [52.5, 0.5, 'G', 5],
  [53.0, 0.5, 'D', 5],
  [53.5, 0.5, null, 0],
  [54.0, 0.5, 'G', 5],
  [54.5, 0.5, 'D', 5],
  [55.0, 1.0, 'B', 4],
  // Bar 15 — Am
  [56.0, 0.5, 'A', 5],
  [56.5, 0.5, 'E', 5],
  [57.0, 0.5, 'C', 5],
  [57.5, 0.5, null, 0],
  [58.0, 0.5, 'E', 5],
  [58.5, 0.5, 'C', 5],
  [59.0, 1.0, 'A', 4],
  // Bar 16 — Am, the resolve back to the top of the loop.
  [60.0, 0.5, 'E', 5],
  [60.5, 0.5, 'D', 5],
  [61.0, 0.5, 'C', 5],
  [61.5, 0.5, 'B', 4],
  [62.0, 0.75, 'A', 4],
  [62.75, 0.25, null, 0],
  [63.0, 1.0, 'A', 4],
];

const MELODY_VOICE: VoiceId = 'square';
const BASS_VOICE: VoiceId = 'saw';
const PAD_VOICE: VoiceId = 'triangle';
const ARP_VOICE: VoiceId = 'square';

function buildMelody(): NoteEvent[] {
  return [
    ...toNotes(MOTIF_A, MELODY_VOICE, 0.85),
    ...toNotes(MOTIF_B, MELODY_VOICE, 0.9),
    // Phrase A′ — the riff's return, bars 9-12 (beat offset 32).
    ...toNotes(MOTIF_A, MELODY_VOICE, 0.85, 32),
    ...toNotes(MOTIF_C, MELODY_VOICE, 0.88),
  ];
}

/**
 * The bassline: root, root, fifth, root, octave-up-of-root, root, fifth,
 * root — eight driving eighth notes per bar, one bar per progression entry.
 * Mechanical on purpose (BASELINE-style: the *pattern* is the composed
 * choice, not sixteen hand-typed bars that would only ever repeat it).
 */
function buildBass(): NoteEvent[] {
  const out: NoteEvent[] = [];
  PROGRESSION.forEach((chordName, barIndex) => {
    const chord = BASS_CHORDS[chordName];
    if (chord === undefined) return;
    const barStart = barIndex * BEATS_PER_BAR;
    const [rootNote, rootOctave] = chord.root;
    const [fifthNote, fifthOctave] = chord.fifth;
    const pattern: readonly [string, number][] = [
      [rootNote, rootOctave],
      [rootNote, rootOctave],
      [fifthNote, fifthOctave],
      [rootNote, rootOctave],
      [rootNote, rootOctave + 1],
      [rootNote, rootOctave],
      [fifthNote, fifthOctave],
      [rootNote, rootOctave],
    ];
    pattern.forEach(([note, octave], step) => {
      out.push({
        beat: barStart + step * 0.5,
        durationBeats: 0.45, // slightly short of a full eighth — a clean, punchy bass, not a legato smear.
        note,
        octave,
        voice: BASS_VOICE,
        velocity: 0.8,
      });
    });
  });
  return out;
}

/** The pad: root+third+fifth together, re-struck every two beats — two hits per bar, four full bars of harmony per phrase. */
function buildChords(): NoteEvent[] {
  const out: NoteEvent[] = [];
  PROGRESSION.forEach((chordName, barIndex) => {
    const chord = PAD_CHORDS[chordName];
    if (chord === undefined) return;
    const barStart = barIndex * BEATS_PER_BAR;
    for (const hitOffset of [0, 2]) {
      for (const [note, octave] of [chord.root, chord.third, chord.fifth]) {
        out.push({
          beat: barStart + hitOffset,
          durationBeats: 2,
          note,
          octave,
          voice: PAD_VOICE,
          velocity: 0.32,
        });
      }
    }
  });
  return out;
}

/** Four-on-the-floor kick, a backbeat snare on 2 and 4, eighth-note hats — one bar pattern, repeated for all sixteen. */
function buildPercussion(): DrumHit[] {
  const out: DrumHit[] = [];
  for (let bar = 0; bar < BAR_COUNT; bar++) {
    const barStart = bar * BEATS_PER_BAR;
    for (let beat = 0; beat < BEATS_PER_BAR; beat++) {
      out.push({ beat: barStart + beat, drum: 'kick', velocity: beat === 0 ? 1 : 0.82 });
    }
    out.push({ beat: barStart + 1, drum: 'snare', velocity: 0.9 });
    out.push({ beat: barStart + 3, drum: 'snare', velocity: 0.9 });
    for (let step = 0; step < 8; step++) {
      out.push({ beat: barStart + step * 0.5, drum: 'hat', velocity: step % 2 === 0 ? 0.55 : 0.4 });
    }
  }
  return out;
}

/**
 * The danger layer: a fast eighth-note arpeggio (root, fifth, third, fifth —
 * repeated twice per bar) over the same harmony, only ever scheduled when
 * `danger.ts`'s `selectMusicLayer` says the stack is high. One octave above
 * the pad, on the bright `square` voice so it cuts through rather than
 * thickening the mix.
 */
function buildDangerLayer(): NoteEvent[] {
  const out: NoteEvent[] = [];
  PROGRESSION.forEach((chordName, barIndex) => {
    const chord = PAD_CHORDS[chordName];
    if (chord === undefined) return;
    const barStart = barIndex * BEATS_PER_BAR;
    const [rootNote, rootOctave] = chord.root;
    const [thirdNote, thirdOctave] = chord.third;
    const [fifthNote, fifthOctave] = chord.fifth;
    const cycle: readonly [string, number][] = [
      [rootNote, rootOctave + 1],
      [fifthNote, fifthOctave + 1],
      [thirdNote, thirdOctave + 1],
      [fifthNote, fifthOctave + 1],
    ];
    for (let rep = 0; rep < 2; rep++) {
      cycle.forEach(([note, octave], step) => {
        out.push({
          beat: barStart + rep * 2 + step * 0.5,
          durationBeats: 0.4,
          note,
          octave,
          voice: ARP_VOICE,
          velocity: 0.5,
        });
      });
    }
  });
  return out;
}

export const COMPOSITION: Composition = {
  title: 'Interlock Drive',
  key: 'A natural minor',
  bpm: BPM,
  beatsPerBar: BEATS_PER_BAR,
  bars: BAR_COUNT,
  melody: buildMelody(),
  bass: buildBass(),
  chords: buildChords(),
  percussion: buildPercussion(),
  dangerLayer: buildDangerLayer(),
  dangerTempoMultiplier: DANGER_TEMPO_MULTIPLIER,
};

/** Every note name/octave pair used anywhere in the composition — for validating against `noteFreq` in tests. */
export function everyPitchedEvent(composition: Composition = COMPOSITION): readonly NoteEvent[] {
  return [
    ...composition.melody,
    ...composition.bass,
    ...composition.chords,
    ...composition.dangerLayer,
  ];
}

/** Re-exported so a test can assert every note name the composition uses is one `noteFreq` accepts. */
export { isNoteName, noteFreq };
